import { ehEncerrado } from '@/leads/encerrados';
import { SEM_ORIGEM, ehSemOrigem } from '@/leads/origens';
import { aparicaoNaJanela } from '@/leads/resgate';
import { semProximoContato } from '@/leads/semProximoContato';

// Filtros combinados do funil (PLANO 4.4). Kanban e lista compartilham este
// mesmo predicado — são duas visões dos mesmos dados, então nunca podem
// divergir na hora de filtrar.

export const FILTROS_VAZIOS = {
  busca: '',
  etapa: '',
  // VQ-1: encerrados (carimbo) ficam fora da lista por padrão; o funil tem coluna própria.
  mostrarEncerrados: false,
  origem: '',
  sdrId: '',
  negociadorId: '',
  temperatura: '',
  // VL: '' (tudo) | '7d' | '30d' | '90d' | 'mes:AAAA-MM' | 'de:AAAA-MM-DD|ate:AAAA-MM-DD' — SEMPRE por criadoEm ("1º contato").
  periodo: '',
  // ★ PC-4: Não agendou / Não compareceu / Não fechou sem nenhuma tarefa aberta. Saíram do
  // "Mais filtros" (decisão do dono, 18/out): Sem telefone, Telefones repetidos, Com valor a
  // pagar e Sem orçamento — `docs/medicao-pc4-filtro-funil.md` §4 e §5.
  semProximoContato: false,
  // VJ: ids de motivo (de Não agendou E de Não fechou) — OU entre eles; vazio = sem filtro.
  motivos: [],
};

// ---------------------------------------------------------------- VL: período por mês / intervalo

/** 'mes:AAAA-MM' do mês de `agora` (local). */
export const mesDe = (agora = new Date()) => `mes:${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;

/**
 * ★ O PADRÃO ao abrir Funil e Lista é o MÊS ATUAL (decisão do dono): depois da
 * V7 "Todo o período" são ~7,5 mil leads. `FILTROS_VAZIOS` continua sendo
 * "tudo" (é o zero do predicado); o padrão inicial é OUTRA constante, montada
 * na hora porque depende do relógio.
 */
export const filtrosIniciais = (agora = new Date()) => ({ ...FILTROS_VAZIOS, periodo: mesDe(agora) });

const MESES_PT = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const rotuloDoMes = (chave) => { const [a, m] = String(chave).replace(/^mes:/, '').split('-').map(Number); return MESES_PT[m - 1] ? `${MESES_PT[m - 1]}/${a}` : chave; };

/**
 * ★ A LIÇÃO DO MEIO-DIA (VC): os limites são montados com
 * `new Date(ano, mes - 1, dia)` — LOCAL. `new Date('2026-03-01')` é UTC e vira
 * 28/02 21:00 em São Paulo: o primeiro dia do mês sumiria do próprio mês.
 */
const inicioLocal = (a, m, d) => new Date(a, m - 1, d, 0, 0, 0, 0);
const fimLocal = (a, m, d) => new Date(a, m - 1, d, 23, 59, 59, 999);
const parteData = (t) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(t ?? '').trim()); return m ? m.slice(1).map(Number) : null; };

/**
 * A janela [inicio, fim] (inclusiva nos dois dias inteiros) de um `periodo`,
 * ou null quando não há recorte. `invertido: true` quando "de" > "até" — as
 * pontas são TROCADAS (nenhum lead some por erro de digitação) e a tela avisa.
 */
export function janelaDoPeriodo(periodo, agora = new Date()) {
  const p = String(periodo ?? '').trim();
  if (!p) return null;
  if (DIAS[p]) return { inicio: new Date(agora.getTime() - DIAS[p] * 86400000), fim: null, invertido: false };
  const mes = /^mes:(\d{4})-(\d{2})$/.exec(p);
  if (mes) { const a = Number(mes[1]); const m = Number(mes[2]); return { inicio: inicioLocal(a, m, 1), fim: fimLocal(a, m + 1, 0), invertido: false }; }
  const de = /(?:^|\|)de:([^|]*)/.exec(p)?.[1]; const ate = /(?:^|\|)ate:([^|]*)/.exec(p)?.[1];
  const pd = parteData(de); const pa = parteData(ate);
  if (!pd && !pa) return null;
  let inicio = pd ? inicioLocal(...pd) : null;
  let fim = pa ? fimLocal(...pa) : null;
  let invertido = false;
  if (inicio && fim && inicio > fim) { const [a2, m2, d2] = pa; const [a1, m1, d1] = pd; inicio = inicioLocal(a2, m2, d2); fim = fimLocal(a1, m1, d1); invertido = true; }
  return { inicio, fim, invertido };
}

/** Lê 'de:…|ate:…' → { de, ate } (strings AAAA-MM-DD ou ''). */
export const partesDoIntervalo = (periodo) => ({ de: /(?:^|\|)de:([^|]*)/.exec(periodo ?? '')?.[1] ?? '', ate: /(?:^|\|)ate:([^|]*)/.exec(periodo ?? '')?.[1] ?? '' });
export const periodoDeIntervalo = (de, ate) => `de:${de ?? ''}|ate:${ate ?? ''}`;
export const ehIntervalo = (periodo) => String(periodo ?? '').startsWith('de:');

/**
 * Os meses que TÊM lead (por criadoEm), do mais recente ao mais antigo — a lista
 * do seletor é derivada dos dados, nunca fixa (não envelhece).
 */
export function mesesComLeads(leads = []) {
  const chaves = new Set();
  for (const l of leads ?? []) { const d = paraData(l?.criadoEm); if (d) chaves.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); }
  return [...chaves].sort().reverse().map((c) => ({ valor: `mes:${c}`, rotulo: rotuloDoMes(c) }));
}

/** Lista de motivos NORMALIZADA: só strings não vazias, sem repetição, ordem estável. */
export const normalizarMotivos = (lista) =>
  [...new Set((Array.isArray(lista) ? lista : []).map((m) => String(m ?? '').trim()).filter(Boolean))];

/**
 * ⚠ Lista no estado: comparar por identidade (`!==`) diria "ativo" para qualquer
 * array, até o vazio recém-criado. Lista conta como filtro quando tem item.
 */
/**
 * Há filtro além do PADRÃO? `base` é o padrão da tela (`filtrosIniciais()`): o
 * mês atual sozinho não acende "Limpar filtros" — mas "Todo o período" acende,
 * porque difere do padrão.
 */
export const temFiltroAtivo = (f, base = FILTROS_VAZIOS) =>
  Object.keys(FILTROS_VAZIOS).some((k) =>
    Array.isArray(FILTROS_VAZIOS[k]) ? normalizarMotivos(f[k]).length > 0 : f[k] !== base[k],
  );

function paraData(valor) {
  if (!valor) return null;
  // Timestamp do Firestore, Date, ou nada (escrita ainda não confirmada).
  if (typeof valor.toDate === 'function') return valor.toDate();
  if (valor instanceof Date) return valor;
  return null;
}

const DIAS = { '7d': 7, '30d': 30, '90d': 90 };

/**
 * ★ PC-4 — `comAberta` é o conjunto de leads com tarefa aberta (`leadsComTarefaAberta`), ou
 * `null` quando as tarefas NÃO estão confiáveis (não chegaram do servidor, ou a assinatura
 * falhou). ⚠⚠ Com `null` o filtro "Sem próximo contato" NÃO é aplicado: calculado sem as
 * tarefas, ele mostraria todo mundo das 3 etapas como "sem ninguém cuidando". A tela avisa.
 */
export function aplicarFiltros(leads, filtros, { comAberta = null } = {}) {
  const busca = filtros.busca.trim().toLowerCase();
  const digitosBusca = busca.replace(/\D/g, '');

  // VL: 7d/30d/90d, mês ou intervalo — sempre por criadoEm, janela inclusiva.
  const janela = janelaDoPeriodo(filtros.periodo);

  // VJ: casa pelo motivo GRAVADO, em qualquer das duas colunas e em qualquer
  // etapa — o motivo fica como histórico quando o lead sai da coluna.
  const motivos = new Set(normalizarMotivos(filtros.motivos));

  return leads.filter((lead) => {
    if (filtros.etapa && lead.etapa !== filtros.etapa) return false;
    // VQ-1: encerrado só aparece com "Mostrar encerrados" ligado.
    if (!filtros.mostrarEncerrados && ehEncerrado(lead)) return false;
    if (motivos.size > 0 && !motivos.has(lead.motivoNaoAgendou) && !motivos.has(lead.motivoNaoFechou)) return false;
    // VH-1: "Sem origem" casa vazio, ausente e legado; um id vivo casa exato.
    if (filtros.origem === SEM_ORIGEM) { if (!ehSemOrigem(lead.origem)) return false; }
    else if (filtros.origem && lead.origem !== filtros.origem) return false;
    if (filtros.sdrId && lead.sdrId !== filtros.sdrId) return false;
    if (filtros.negociadorId && lead.negociadorId !== filtros.negociadorId) return false;
    if (filtros.temperatura && lead.temperatura !== filtros.temperatura) return false;
    // ★ PC-4: só com as tarefas confiáveis (ver o comentário da função).
    if (filtros.semProximoContato && comAberta && !semProximoContato(lead, comAberta)) return false;

    // VR: na janela do período o lead aparece como NOVO (criadoEm) ou RESGATE (mês em `resgates`);
    // sem janela, uma aparição só. Quem decide é a regra pura.
    if (janela && !aparicaoNaJanela(lead, janela)) return false;

    if (busca) {
      const alvo = [lead.nome, lead.email, lead.procedimento, lead.cidade]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const telefone = (lead.telefone ?? '').replace(/\D/g, '');
      const casaTexto = alvo.includes(busca);
      const casaTelefone = digitosBusca.length >= 3 && telefone.includes(digitosBusca);
      if (!casaTexto && !casaTelefone) return false;
    }

    return true;
  });
}
